/**
 * <TokenLinkDisplay> — shared "reveal" panel used by Wave-4 modals that
 * surface a freshly-minted invite / reset URL to the actor:
 *
 *   - employees-add.modal     (24h invite link, post-create step 2)
 *   - employees-reissue-invite.dialog (24h invite link)
 *   - employees-reset-password.dialog (1h reset link)
 *
 * Responsibilities (research-A §2.1/§2.4/§2.5; PLAN W4.T3/T5/T6):
 *
 *   1. Render the absolute URL in a read-only input the user can select.
 *   2. Provide a Copy-to-Clipboard button with success toast.
 *   3. Show a live countdown to `expiresAt`, ticking every second; the
 *      ticker stops at 00:00:00 and switches the label to «Истекло».
 *   4. Optional secondary action slot (used by the reset dialog to add
 *      «Отправить по email» next to «Скопировать»).
 *
 * Why a shared component: the three modals share the entire "reveal"
 * affordance — extracting it both removes duplication and makes the
 * countdown logic one easy thing to test in isolation. The countdown
 * uses `Date.now()` so vitest's fake timers in the modal tests can
 * advance it deterministically.
 */
import * as React from 'react';
import { Copy } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from '@/components/ui/toast';
import { cn } from '@/lib/utils';

/**
 * Build an absolute URL the user can paste into a chat / email. Server
 * payloads typically return a relative path (e.g. `/invite/raw-xxx`); we
 * prefix the page origin so the link works when shared off-platform.
 */
export function toAbsoluteLink(urlOrPath: string): string {
  if (/^https?:\/\//i.test(urlOrPath)) return urlOrPath;
  if (typeof window === 'undefined') return urlOrPath;
  const base = window.location.origin.replace(/\/$/, '');
  return `${base}${urlOrPath.startsWith('/') ? '' : '/'}${urlOrPath}`;
}

/**
 * Format a remaining-millis duration as `HH:mm:ss`. Negative values clamp
 * to 0 (the calling component reports «Истекло» for that case).
 */
export function formatCountdown(ms: number): string {
  const safe = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(safe / 3600);
  const m = Math.floor((safe % 3600) / 60);
  const s = safe % 60;
  const pad = (n: number): string => String(n).padStart(2, '0');
  return `${pad(h)}:${pad(m)}:${pad(s)}`;
}

interface CountdownState {
  remainingMs: number;
  expired: boolean;
}

function useCountdown(expiresAtIso: string): CountdownState {
  const expiresAtMs = React.useMemo(() => Date.parse(expiresAtIso), [expiresAtIso]);
  const compute = React.useCallback((): CountdownState => {
    const remaining = Number.isFinite(expiresAtMs) ? expiresAtMs - Date.now() : 0;
    return { remainingMs: Math.max(0, remaining), expired: remaining <= 0 };
  }, [expiresAtMs]);
  const [state, setState] = React.useState<CountdownState>(compute);

  React.useEffect(() => {
    setState(compute());
    const handle = window.setInterval(() => {
      setState(compute());
    }, 1000);
    return () => window.clearInterval(handle);
  }, [compute]);

  return state;
}

export interface TokenLinkDisplayProps {
  /** Absolute or relative URL — relative paths are joined to window.origin. */
  url: string;
  /** ISO-8601 expiry timestamp from the BE. Drives the countdown. */
  expiresAt: string;
  /** Label rendered above the input. Defaults to «Ссылка». */
  label?: string;
  /** Optional secondary action button (e.g. «Отправить по email»). */
  secondaryAction?: React.ReactNode;
  /** test-id stable across modals; defaults to "token-link-display". */
  testIdPrefix?: string;
  className?: string;
}

/**
 * Read-only reveal panel. Selectable URL + copy button + live countdown.
 * Falls back gracefully when `navigator.clipboard` is unavailable (server-
 * render, jsdom in CI without a permission policy) — the input remains
 * selectable so the user can still copy by hand.
 */
export function TokenLinkDisplay(props: TokenLinkDisplayProps): React.ReactElement {
  const {
    url,
    expiresAt,
    label = 'Ссылка',
    secondaryAction,
    testIdPrefix = 'token-link-display',
    className,
  } = props;
  const absoluteUrl = React.useMemo(() => toAbsoluteLink(url), [url]);
  const { remainingMs, expired } = useCountdown(expiresAt);

  const handleCopy = React.useCallback(async () => {
    const clipboard = (typeof navigator !== 'undefined' ? navigator.clipboard : undefined) as
      | Clipboard
      | undefined;
    if (!clipboard?.writeText) {
      // Insecure context (http://) or browser without Clipboard API — the
      // URL is still selectable on screen, so guide the user to copy by hand.
      toast('Скопируйте ссылку вручную', { variant: 'info' });
      return;
    }
    try {
      await clipboard.writeText(absoluteUrl);
      toast('Ссылка скопирована', { variant: 'success' });
    } catch {
      // Permission denied / blocked despite the API being present (e.g.
      // user denied the prompt, focus loss). The URL is still visible on
      // screen — surface the failure explicitly so the actor knows the
      // copy didn't happen and falls back to manual select+copy.
      toast('Не удалось скопировать. Скопируйте вручную.', { variant: 'warning' });
    }
  }, [absoluteUrl]);

  return (
    <div className={cn('space-y-2', className)} data-testid={testIdPrefix}>
      <label className="block text-xs font-medium text-muted">{label}</label>
      <div className="flex gap-2">
        <Input
          readOnly
          value={absoluteUrl}
          aria-label={label}
          className="flex-1 font-mono text-[12px]"
          data-testid={`${testIdPrefix}-url`}
          onFocus={(e) => e.currentTarget.select()}
        />
        <Button
          type="button"
          variant="outline"
          size="md"
          onClick={handleCopy}
          data-testid={`${testIdPrefix}-copy`}
          aria-label="Скопировать ссылку"
        >
          <Copy className="size-4" aria-hidden="true" />
          <span>Скопировать</span>
        </Button>
        {secondaryAction}
      </div>
      <p className="text-xs text-muted" data-testid={`${testIdPrefix}-countdown`}>
        {expired ? (
          <span className="text-destructive">Срок действия истёк</span>
        ) : (
          <>
            Действительна ещё <span className="font-mono">{formatCountdown(remainingMs)}</span>
          </>
        )}
      </p>
    </div>
  );
}
